/**
 * Chat attachment helpers.
 *
 * Background: previously we stored a long-lived signed URL in
 * `chat_messages.file_url`. When signing failed silently we ended up writing
 * an empty string, which rendered as a blank, unclickable bubble. Even when
 * signing succeeded the URL was tied to the issuer session and would stop
 * working after token rotation.
 *
 * Going forward we store the bucket-relative **path** (e.g.
 * `support/<convo>/<file>.jpg`) prefixed with the marker `path:` and resolve
 * it to a fresh signed URL on demand. Legacy rows that already contain a
 * full https URL keep working unchanged.
 */
import { supabase } from "@/integrations/supabase/client";
import type { SupabaseClient } from "@supabase/supabase-js";

export const CHAT_BUCKET = "chat-attachments";
const PATH_PREFIX = "path:";

/** Format a storage path so we can detect it later when rendering. */
export function encodeAttachmentPath(path: string): string {
  return `${PATH_PREFIX}${path}`;
}

/** True when a stored value points to a storage object (vs. a legacy full URL). */
export function isAttachmentPath(value: string | null | undefined): boolean {
  if (!value) return false;
  return value.startsWith(PATH_PREFIX);
}

/** Strip the `path:` marker. */
export function decodeAttachmentPath(value: string): string {
  return value.startsWith(PATH_PREFIX) ? value.slice(PATH_PREFIX.length) : value;
}

const cache = new Map<string, { url: string; expires: number }>();
const inflight = new Map<string, Promise<string | null>>();

/**
 * Resolve a stored `file_url` to a usable URL.
 *  - Legacy full URLs (`https://…`) are returned as-is.
 *  - `path:<key>` markers are signed against the chat-attachments bucket.
 *  - Returns null when signing fails so the caller can render a fallback.
 */
export async function resolveChatAttachmentUrl(
  fileUrl: string | null | undefined,
  client: SupabaseClient = supabase,
): Promise<string | null> {
  if (!fileUrl) return null;
  if (!isAttachmentPath(fileUrl)) return fileUrl;
  const path = decodeAttachmentPath(fileUrl);
  const cached = cache.get(path);
  const now = Date.now();
  if (cached && cached.expires > now + 30_000) return cached.url;
  if (inflight.has(path)) return inflight.get(path)!;
  const promise = (async () => {
    const { data, error } = await client.storage.from(CHAT_BUCKET).createSignedUrl(path, 60 * 60);
    if (error || !data?.signedUrl) return null;
    cache.set(path, { url: data.signedUrl, expires: now + 60 * 60 * 1000 });
    return data.signedUrl;
  })();
  inflight.set(path, promise);
  try { return await promise; } finally { inflight.delete(path); }
}
